import { prepareMasterPasswordChange } from '@/crypto/key-hierarchy'
import { ApiError } from '@services/apiClient'
import { prelogin as defaultPrelogin } from '@features/auth/api/authApi'
import { sessionStore } from '@features/auth/services/sessionStore'
import { changeMasterPassword as defaultChange } from '../api/masterPasswordApi'

/**
 * Master password 変更フロー (Track E-1)。
 *
 * 設計判断:
 *   - sessionStore.getState() === 'unlocked' を要求。SK + material.email を取得して
 *     crypto.prepareMasterPasswordChange に渡す。
 *   - 入力検証 (長さ / 確認一致) はここで実施。文言は messageKey で返し、UI 側で
 *     `t()` 適用する (CLAUDE.md §2 ルール 10、useLoginForm と同じ pattern)。
 *   - 成功時:
 *       1. `sessionStore.updateAccessToken` で新 access token を反映
 *       2. material.kdfSalt / kdfParams / protectedSymmetricKey / IV を新値で
 *          上書きする (LOCKED 時に新 MP で unlock するため)
 *     SK は引き続き同じ Uint8Array (zeroize しない)。Vault Item / Send / Recovery
 *     は影響なし
 *   - 失敗時 (401 / 5xx / network) は messageKey を error で返し、sessionStore に
 *     一切触らない
 *   - deps DI で network を spec で差し替え可能
 */

export class MasterPasswordError extends Error {
  public readonly code:
    | 'no_session'
    | 'password_too_short'
    | 'password_mismatch'
    | 'wrong_old_password'
    | 'too_many_requests'
    | 'network'
    | 'generic'

  public constructor(code: MasterPasswordError['code']) {
    super(code)
    this.code = code
    this.name = 'MasterPasswordError'
  }
}

export const MIN_PASSWORD_LENGTH = 12

export interface MasterPasswordFlowDeps {
  prelogin: typeof defaultPrelogin
  change: typeof defaultChange
}

const defaultDeps: MasterPasswordFlowDeps = {
  prelogin: defaultPrelogin,
  change: defaultChange,
}

export interface ChangeMasterPasswordInput {
  oldPassword: string
  newPassword: string
  newPasswordConfirm: string
}

/**
 * Master password を変更する。成功で sessionStore を更新、失敗で MasterPasswordError を throw。
 */
export const changeMasterPasswordFlow = async (
  input: ChangeMasterPasswordInput,
  deps: MasterPasswordFlowDeps = defaultDeps,
): Promise<void> => {
  const { oldPassword, newPassword, newPasswordConfirm } = input

  if (newPassword.length < MIN_PASSWORD_LENGTH) {
    throw new MasterPasswordError('password_too_short')
  }
  if (newPassword !== newPasswordConfirm) {
    throw new MasterPasswordError('password_mismatch')
  }

  const state = sessionStore.getState()
  if (state.kind !== 'unlocked') {
    throw new MasterPasswordError('no_session')
  }

  const email = state.material.email

  // ── 1. prelogin で現 MP に紐づく SRP session を取得 ──
  // sessionId は 5 分 TTL、SRP-6a 標準。
  const preloginResponse = await deps.prelogin(email)

  // ── 2. crypto.prepareMasterPasswordChange で SRP M1 + 新 fields を組立 ──
  const request = await prepareMasterPasswordChange({
    email,
    oldPassword,
    newPassword,
    symmetricKey: state.symmetricKey,
    prelogin: preloginResponse,
  })

  // ── 3. PUT /api/users/me/master-password ──
  let response
  try {
    response = await deps.change(request)
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      if (err.status === 401) throw new MasterPasswordError('wrong_old_password')
      if (err.status === 429) throw new MasterPasswordError('too_many_requests')

      throw new MasterPasswordError('network')
    }
    throw new MasterPasswordError('generic')
  }

  // ── 4. sessionStore を更新 (新 access token + 新 material) ──
  // SK の中身は不変だが、`sessionStore.set` は既存 SK を `zeroize` してから新値を
  // 置く実装のため、**同じ Uint8Array reference を渡すと中身が 0 になってしまう**。
  // クローンして渡すことで、旧参照だけ zeroize され、新セッションは清潔な SK を
  // 持つ (内容物は同じ 32 byte なので Vault Item / Send / Recovery は影響なし)。
  const skCopy = new Uint8Array(state.symmetricKey)

  sessionStore.set({
    accessToken: response.accessToken,
    symmetricKey: skCopy,
    material: {
      kdfSalt: request.kdfSalt,
      kdfParams: {
        memoryCost: request.kdfMemoryKib,
        timeCost: request.kdfTimeCost,
        parallelism: request.kdfParallelism,
      },
      protectedSymmetricKey: request.protectedSymmetricKey,
      protectedSymmetricKeyIv: request.protectedSymmetricKeyIv,
      email,
    },
  })
}
